import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineAll } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	combineLatest,
	filter,
	map,
	of,
	switchMap,
	tap,
	withLatestFrom,
} from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AjsScopeService } from '../../ajs-scope.service';
import { AjsStateService } from '../../ajs-state.service';
import { TagAdapter } from '../../core/adapters/tag.adapter';
import { states } from '../../core/application.states';
import { setGloballyAvailableTags } from '../../core/store';
import { TagRequester } from '../../requesters';
import { CharacteristicTypeRequester } from '../../requesters/core/characteristic-type.requester';
import { DateFormatRequester } from '../../requesters/core/date-format.requester';
import { LanguageRequester } from '../../requesters/core/language.requester';
import { TimezoneRequester } from '../../requesters/core/timezone.requester';
import { IDepotSchemaModel } from '../../requesters/depot/depot.model';
import { DepotRequester } from '../../requesters/depot/depot.requester';
import { EmailSenderRequester } from '../../requesters/email/email-sender.requester';
import {
	ISendEmailOutput,
	SendEmailPopupComponent,
} from '../../ui/components/send-email-popup/send-email-popup.component';
import {
	ChangeVersionModalComponent,
	ChangeVersionModalData,
} from '../components/change-version-modal/change-version-modal.component';
import { CreateEntityFromDepotModalComponent } from '../components/create-entity-from-depot-modal/create-entity-from-depot-modal.component';
import { DepotCreationModalComponent } from '../components/depot-creation-modal/depot-creation-modal.component';
import { DepotItemEntity } from '../components/depot-list/depot-item.entity';
import {
	DepotSchemaEntity,
	SchemaSelectedHeader,
} from '../components/depot-schema-viewer/depot-schema.entity';
import { AppHeaderMappingModalComponent } from '../components/header-mapping-modal/header-mapping-modal.component';
import { DepotAdapter } from '../depot.adapter';
import { DepotSchemaConfig } from '../domain/depot-schema-config';
import {
	bulkDeleteDepots,
	bulkDeleteDepotsFail,
	bulkDeleteDepotsSuccess,
	changeVersionForSelectedDataLinkRepo,
	changeVersionForSelectedDataLinkRepoFail,
	changeVersionForSelectedDataLinkRepoSuccess,
	closeCreateDepotModal,
	contactDataLinkEditorsFail,
	contactDataLinkEditorsSuccess,
	createEntityFromDepot,
	createEntityFromDepotFail,
	createEntityFromDepotSuccess,
	createSubscriptionForRepository,
	createSubscriptionForRepositoryFail,
	createSubscriptionForRepositorySuccess,
	deleteDepot,
	deleteDepotFail,
	deleteDepotSuccess,
	DepotActions,
	fetchAvailableVersionForSelectedDataLinkRepoFail,
	fetchAvailableVersionForSelectedDataLinkRepoSuccess,
	fetchDepotHeadersFail,
	fetchDepotHeadersSuccess,
	fetchDepotListFail,
	fetchDepotListSuccess,
	fetchDepotSchemaMappingError,
	fetchDepotSchemaMappingSuccess,
	getDepotHeaderSettingFail,
	getDepotHeaderSettingSuccess,
	initAvailableDepotHeadersForEntityCreation,
	initDepotHeaderSetting,
	initDepotList,
	navigateToDepot,
	navigateToDepotCreate,
	navigateToDepotCreateModal,
	navigateToDepotDuplicate,
	openChangeVersionModal,
	openCreateDepotModal,
	openCreateEntityFromDepotModal,
	openDepotHeaderFieldsSelection,
	openSendEmailPopup,
	reconstructDepotSchemaMappingError,
	reconstructDepotSchemaMappingSuccess,
	refreshDepotList,
	toggleDepotStatus,
	triggerDetectAvailableHeaders,
	updateEntitiesCountForDepot,
	updateEntitiesCountForDepotFailure,
	updateEntitiesCountForDepotSuccess,
} from './depot.actions';
import { DepotSelector } from './depot.selector';

@Injectable()
export class DepotEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################

	public navigateToDepotCreate = createEffect(
		() =>
			this.actions$.pipe(
				ofType(navigateToDepotCreate),
				tap(() => this.stateService.go(states.DepotAngularJSCreatePage))
			),
		{
			dispatch: false,
		}
	);

	public navigateToDepotCreateModal = createEffect(
		() =>
			this.actions$.pipe(
				ofType(navigateToDepotCreateModal),
				tap(() => this.stateService.go(states.DepotCreateModal))
			),
		{
			dispatch: false,
		}
	);

	public navigateToDepot = createEffect(
		() =>
			this.actions$.pipe(
				ofType(navigateToDepot, createSubscriptionForRepositorySuccess),
				tap((a) =>
					this.stateService.go(states.DepotAngularJsEditPage, {
						id: `${a.id}`,
						type: `${a.depotType}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	public navigateToDepotDuplicate = createEffect(
		() =>
			this.actions$.pipe(
				ofType(navigateToDepotDuplicate),
				tap((a) =>
					this.stateService.go(states.DepotDuplicate, {
						duplicatedId: `${a.id}`,
						type: a.depotType,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################
	// Business Effects
	// ##########################################################

	public initDepotList$ = createEffect(() =>
		this.actions$.pipe(
			ofType(initDepotList, refreshDepotList),
			switchMap((action) => {
				if (action.type === DepotActions.RefreshDepotList) {
					return this.depotRequester.getDepotList(false);
				}
				return this.depotRequester.getDepotList(true);
			}),
			map((response) =>
				fetchDepotListSuccess({
					depotList: this.depotAdapter.generateDepotList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchDepotListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getEntitiesCountForDepot$ = createEffect(() =>
		this.actions$.pipe(
			ofType(updateEntitiesCountForDepot),
			switchMap((action) =>
				this.depotRequester.entitiesByDepotBulkCount(
					action.ids,
					!action.shouldReload
				)
			),
			withLatestFrom(this.depotSelector.getDepotList$()),
			map(([response, currentList]) =>
				updateEntitiesCountForDepotSuccess({
					depotList: this.depotAdapter.updateListWithEntityCount(
						currentList,
						response
					),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					updateEntitiesCountForDepotFailure({
						error,
					})
				);
				return caught;
			})
		)
	);

	public deleteDepots$ = createEffect(() =>
		this.actions$.pipe(
			ofType(deleteDepot),
			withLatestFrom(this.depotSelector.getDepotList$()),
			switchMap(([action, list]) =>
				this.depotRequester.deleteDepot(
					action.depotId,
					(list.elements.get(action.depotId.toString()) as DepotItemEntity).type
				)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@depot.delete.success:`
				);
				return deleteDepotSuccess();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@depot.delete.error:`
				);
				this.store.dispatch(deleteDepotFail());
				return caught;
			})
		)
	);

	public bulkDeleteDepots$ = createEffect(() =>
		this.actions$.pipe(
			ofType(bulkDeleteDepots),
			switchMap((action) => this.depotRequester.deleteMany(action.depotIds)),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@depot.delete.multiple.success:`
				);
				return bulkDeleteDepotsSuccess();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@depot.delete.multiple.error:`
				);
				this.store.dispatch(bulkDeleteDepotsFail());
				return caught;
			})
		)
	);

	public toggleActivationStatus$ = createEffect(() =>
		this.actions$.pipe(
			ofType(toggleDepotStatus),
			switchMap((action) =>
				this.depotRequester.changeActivationStatusDepot(
					action.depotId,
					action.depotType,
					action.status
				)
			),
			map(() => refreshDepotList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshDepotList());
				return caught;
			})
		)
	);

	public fetchAvailableTags$ = createEffect(() =>
		this.actions$.pipe(
			ofType(initAvailableDepotHeadersForEntityCreation),
			switchMap(() => this.tagRequester.getAvailableTags(true)),
			map((resp) => this.tagAdapter.generateTags(resp)),
			map((res) =>
				setGloballyAvailableTags({
					availableTags: res,
				})
			)
		)
	);

	public initAvailableDepotHeadersForEntityCreation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(initAvailableDepotHeadersForEntityCreation),
			switchMap((action) =>
				combineAll({
					availableCharacteristicTypes:
						this.characteristicTypeRequester.getAvailableTypes(),
					availableDateFormats:
						this.dateFormatRequester.getAvailableDateFormats(),
					availableTimezones: this.timezoneRequester.getAvailableTimezones(),
					supportedLanguages: this.languageRequester.getSupportedLanguages(),
					headersForSelectRepo: this.depotRequester.getDepotHeaders(
						action.depotId,
						action.depotType,
						true
					),
				})
			),
			map(
				({
					availableCharacteristicTypes,
					availableDateFormats,
					availableTimezones,
					supportedLanguages,
					headersForSelectRepo,
				}) =>
					fetchDepotHeadersSuccess({
						headers:
							this.depotAdapter.generateDepotHeaderList(headersForSelectRepo),
						availableCharacteristicTypes:
							this.depotAdapter.generateAvailableTypes(
								availableCharacteristicTypes
							),
						availableDateFormats:
							this.depotAdapter.generateAvailableDateFormats(
								availableDateFormats
							),
						availableTimezones,
						supportedLanguages:
							this.depotAdapter.generateSupportedLanguages(supportedLanguages),
					})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchDepotHeadersFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public createEntityFromDepot$ = createEffect(() =>
		this.actions$.pipe(
			ofType(createEntityFromDepot),
			withLatestFrom(
				this.depotSelector.getAvailableCharacteristicTypes$(),
				this.depotSelector.getHeadersForSelectRepo$()
			),
			switchMap(([action, types, list]) =>
				this.depotRequester.createEntityFromDepot(
					action.depotId,
					action.metadata,
					action.headers,
					list.elements.toList().toArray(),
					types.toList().toArray()
				)
			),
			map((identifier) => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@depot.modal.entity.create.success:`
				);
				this.ajsScopeService.broadcastOnRootScope(
					'linked-entity:list:refresh',
					identifier
				);

				return createEntityFromDepotSuccess();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@depot.modal.entity.create.error:`
				);
				this.store.dispatch(
					createEntityFromDepotFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public goToDepotListAfterCreate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(createEntityFromDepotSuccess),
			map(() => refreshDepotList()),
			tap(() =>
				this.modalService.closeActiveModal({
					result: 'success',
				})
			)
		)
	);

	public initDepotHeaderSetting$ = createEffect(() =>
		this.actions$.pipe(
			ofType(initDepotHeaderSetting),
			switchMap(() =>
				combineAll({
					availableTypes: this.characteristicTypeRequester.getAvailableTypes(),
					availableDateFormats:
						this.dateFormatRequester.getAvailableDateFormats(),
					availableTimezones: this.timezoneRequester.getAvailableTimezones(),
					supportedLanguages: this.languageRequester.getSupportedLanguages(),
				})
			),
			map(
				({
					availableTypes,
					availableDateFormats,
					availableTimezones,
					supportedLanguages,
				}) =>
					getDepotHeaderSettingSuccess({
						availableCharacteristicTypes:
							this.depotAdapter.generateAvailableTypes(availableTypes),
						availableDateFormats:
							this.depotAdapter.generateAvailableDateFormats(
								availableDateFormats
							),
						availableTimezones,
						supportedLanguages:
							this.depotAdapter.generateSupportedLanguages(supportedLanguages),
					})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					getDepotHeaderSettingFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public createSubscriptionForRepository$ = createEffect(() =>
		this.actions$.pipe(
			ofType(createSubscriptionForRepository),
			switchMap((action) =>
				this.depotRequester.createRepository(
					action.metadata,
					'DATALINK',
					action.subscription,
					action.allowProducerVersionSwitch
				)
			),
			tap(() =>
				this.modalService.closeActiveModal({
					result: 'success',
				})
			),
			map((response) => {
				if (response?.id === undefined || response?.type === undefined) {
					throw new Error('Invalid response: missing id or type');
				}
				return createSubscriptionForRepositorySuccess({
					id: response.id,
					depotType: response.type,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					createSubscriptionForRepositoryFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getAvailableVersionsForSelectedDataLinkRepo$ = createEffect(() =>
		this.actions$.pipe(
			ofType(openChangeVersionModal),
			switchMap((action) =>
				this.depotRequester.getAvailableVersionsForDataLinkRepo(action.depot.id)
			),
			map((response) =>
				fetchAvailableVersionForSelectedDataLinkRepoSuccess({
					versions:
						this.depotAdapter.generateAvailableVersionsForDataLinkRepo(
							response
						),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchAvailableVersionForSelectedDataLinkRepoFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public changeVersionsForSelectedDataLinkRepo$ = createEffect(() =>
		this.actions$.pipe(
			ofType(changeVersionForSelectedDataLinkRepo),
			switchMap((action) =>
				this.depotRequester.changeVersionForDataLinkRepo(
					action.depotId,
					action.newVersion
				)
			),
			map(() => changeVersionForSelectedDataLinkRepoSuccess()),
			tap(() => {
				this.modalService.closeActiveModal();
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@depot.changeVersion.action.success:`
				);
				setTimeout(() => {
					window.location.reload();
				}, 1000);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@depot.changeVersion.action.error:`
				);
				this.store.dispatch(
					changeVersionForSelectedDataLinkRepoFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public navigateToListAfterClosed$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(closeCreateDepotModal),
				tap(() => {
					this.modalService.closeActiveModal();
					this.stateService.go(states.DepotList);
				})
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################

	public openCreateEntityFromDepotModal$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(openCreateEntityFromDepotModal),
				map((action) =>
					this.modalService.openModal<{
						depotId: number;
						depotLabel: string;
						depotType: string;
					}>(
						CreateEntityFromDepotModalComponent,
						{
							title: $localize`:i18n=@@depot.modal.create.entity.header:`,
							subTitle: '',
							depotId: action.depotId,
							depotType: action.depotType,
							depotLabel: action.depotLabel,
						},
						{
							disableClose: true,
						}
					)
				)
			),
		{
			dispatch: false,
		}
	);

	public openCreateDepotModal$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(openCreateDepotModal),
				map(() =>
					this.modalService.openModal(
						DepotCreationModalComponent,
						{
							title: $localize`:i18n=@@depot.modal.create:`,
							subTitle: '',
						},
						{
							disableClose: true,
						}
					)
				)
			),
		{
			dispatch: false,
		}
	);

	public openChangeVersionModal$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(openChangeVersionModal),
				map((action) =>
					this.modalService.openPopup<ChangeVersionModalData>(
						ChangeVersionModalComponent,
						{
							title: $localize`:i18n=@@depot.changeVersion.modal.header:`,
							subTitle: '',
							width: '60%',
							depot: action.depot,
						},
						{
							disableClose: true,
						}
					)
				)
			),
		{
			dispatch: false,
		}
	);

	public openSendEmailPopup$ = createEffect(() =>
		this.actions$.pipe(
			ofType(openSendEmailPopup),
			switchMap((action) => {
				const modalRef = this.modalService.openPopup(
					SendEmailPopupComponent,
					{
						title: $localize`:i18n=@@depot.datalink.contact.popup.header:`,
						subTitle: '',
					},
					{
						disableClose: true,
					}
				);
				return combineLatest([of(action), modalRef.afterClosed()]);
			}),
			// Fermeture sans envoi : rien a ecrire aux responsables.
			filter(([, modalResult]) => modalResult !== undefined),
			switchMap(([action, modalResult]) => {
				const { title, message } = modalResult as ISendEmailOutput;
				return this.emailSenderRequester.contactDataLinkEditors(
					action.depotId,
					{
						subject: title,
						body: message,
					}
				);
			}),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@depot.datalink.contact.success:`
				);
				return contactDataLinkEditorsSuccess();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@depot.datalink.contact.error:`
				);
				this.store.dispatch(
					contactDataLinkEditorsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public openDepotHeaderFieldsSelection$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(openDepotHeaderFieldsSelection),
				switchMap((action) => {
					let subTitle = '';
					switch (action.params.depotType) {
						case 'datalink':
							subTitle = $localize`:i18n=@@depot.type.subscription:`;
							break;
						case 'trino':
							subTitle = $localize`:i18n=@@depot.type.trino:`;
							break;
						case 'json':
							subTitle = $localize`:i18n=@@depot.type.json:`;
							break;
						case 'xml':
							subTitle = $localize`:i18n=@@depot.type.xml:`;
							break;
					}
					const modalRef = this.modalService.openModal<{
						depotConfig: DepotSchemaConfig;
					}>(
						AppHeaderMappingModalComponent,
						{
							title: $localize`:i18n=@@depot.headers.mapping:`,
							subTitle,
							depotConfig: action.params,
						},
						{
							disableClose: true,
						}
					);
					return modalRef.afterClosed();
				}),
				map(
					(modalData: {
						depotMapping: Array<DepotSchemaEntity> | null;
						selectedHeaders: Array<SchemaSelectedHeader>;
					}) => {
						if (modalData.depotMapping === null) {
							return null;
						}
						return {
							selectedHeaders: modalData.selectedHeaders,
							depotMapping: this.depotAdapter.generateDepotSchemaHeaderPayload(
								modalData.depotMapping
							),
						};
					}
				),
				tap((payload) => {
					this.ajsScopeService.broadcastOnRootScope('depot:mapping:', payload);
				})
			),
		{
			dispatch: false,
		}
	);

	public fetchAvailableHeader$ = createEffect(() =>
		this.actions$.pipe(
			ofType(openDepotHeaderFieldsSelection),
			switchMap((action) =>
				// perform request before opening modal to prevent transporting data needlessly to the component
				// uses retrieved schema in edit mode
				action.currentSchema
					? of({
							schema: action.currentSchema,
							headers: [],
					  })
					: this.depotRequester.getSchema(action.params)
			),
			map((payload) => {
				const mapping = this.depotAdapter.generateDepotSchemaHeaderMapping(
					payload as IDepotSchemaModel
				);
				return reconstructDepotSchemaMappingSuccess({
					mapping,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					reconstructDepotSchemaMappingError({
						error,
					})
				);
				return caught;
			})
		)
	);

	public detectAvailableHeader$ = createEffect(() =>
		this.actions$.pipe(
			ofType(triggerDetectAvailableHeaders),
			switchMap((action) =>
				// perform request before opening modal to prevent transporting data needlessly to the component
				this.depotRequester.getSchema(action.params)
			),
			map((payload) => {
				const mapping = this.depotAdapter.generateDepotSchemaHeaderMapping(
					payload as IDepotSchemaModel
				);
				return fetchDepotSchemaMappingSuccess({
					mapping,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchDepotSchemaMappingError({
						error,
					})
				);
				return caught;
			})
		)
	);

	public constructor(
		private readonly actions$: Actions,
		private readonly depotSelector: DepotSelector,
		private readonly depotRequester: DepotRequester,
		private readonly emailSenderRequester: EmailSenderRequester,
		private readonly characteristicTypeRequester: CharacteristicTypeRequester,
		private readonly dateFormatRequester: DateFormatRequester,
		private readonly timezoneRequester: TimezoneRequester,
		private readonly languageRequester: LanguageRequester,
		private readonly tagRequester: TagRequester,
		private readonly depotAdapter: DepotAdapter,
		private readonly tagAdapter: TagAdapter,
		private readonly stateService: AjsStateService,
		private readonly store: Store,
		private readonly modalService: AppModalService,
		private readonly ngCommService: NgCommService,
		private readonly ajsScopeService: AjsScopeService
	) {}
}
